import { SortOrder } from '@/constants/request';
import { CHAT_HISTORY_LIST_TAG, LIST_QUERY_CACHE_TTL_SECONDS } from '@/api/constants';
import { api } from '@/api/core';
import type { ChatHistoryData } from '@/api/chat/getChatHistory';
import { queryMockProjectChats } from './mockProjectChats';
import { mockDelay } from './mockUtils';

/** A file the chat saved to the project. */
export interface ProjectChatArtefact {
  fileId: string;
  fileName: string;
}

/** Shape assumed until the backend contract exists */
export interface ProjectChatData extends ChatHistoryData {
  messageCount: number;
  artefacts: ProjectChatArtefact[];
}

export const PROJECT_CHAT_SORT_FIELDS = ['latestActivity', 'messageCount'] as const;

export type ProjectChatSortField = (typeof PROJECT_CHAT_SORT_FIELDS)[number];

export interface GetProjectChatsRequest {
  projectId: string;
  pageIndex: number;
  pageSize: number;
  sortBy?: ProjectChatSortField;
  sortOrder?: SortOrder;
  /** Matched against the chat title only. */
  search?: string;
  /** Names of the users who started the chats. */
  startedBy?: string[];
}

export interface GetProjectChatsResponse {
  chats: ProjectChatData[];
  numberOfResults: number;
  /** Everyone who started a chat in the project, for the Started by filter. */
  startedByOptions: string[];
}

const MOCK_RESPONSE_DELAY_MS = 400;

const getProjectChatsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getProjectChats: builder.query<GetProjectChatsResponse, GetProjectChatsRequest>({
      // The backend endpoint for a project's chats does not exist yet: serve mock data (see mockProjectChats.ts).
      queryFn: async (args) => {
        await mockDelay(MOCK_RESPONSE_DELAY_MS);
        const response = queryMockProjectChats(args);

        return response ? { data: response } : { error: { status: 404, data: 'Project not found' } };
      },
      // Refetched when a chat is deleted.
      providesTags: () => [{ type: CHAT_HISTORY_LIST_TAG }],
      keepUnusedDataFor: LIST_QUERY_CACHE_TTL_SECONDS,
    }),
  }),
});

export const { useGetProjectChatsQuery } = getProjectChatsApi;
